import { AnimatePresence, motion } from "framer-motion"
import { ChevronDown } from "lucide-react"
import type React from "react"
import { memo, useCallback } from "react"

interface FAQAccordionProps {
  id: string
  question: string
  answer: string | React.JSX.Element
  isOpen: boolean
  onToggle: (id: string) => void
  updatedAt?: string
}

export const FAQAccordion = memo(function FAQAccordion({
  id,
  question,
  answer,
  isOpen,
  onToggle,
  updatedAt,
}: FAQAccordionProps) {
  const handleClick = useCallback(() => {
    onToggle(id)
  }, [id, onToggle])

  // Helper: format a date into a concise relative time
  const getRelativeTime = (date: Date) => {
    const diffMs = Date.now() - date.getTime()
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))
    return diffDays === 0 ? "today" : `${diffDays}d`
  }

  const relativeTime = updatedAt
    ? getRelativeTime(new Date(updatedAt))
    : getRelativeTime(new Date("2025-08-08T00:00:00Z"))

  const updateText = relativeTime === "today" ? "Updated today" : `Updated ${relativeTime.replace("month", "mo")} ago`

  return (
    <div
      id={`faq-${id}`}
      className={`bg-linear-to-r from-white/5 to-white/2 rounded-2xl overflow-hidden backdrop-blur-md relative z-70 border transition-colors duration-150 ${
        isOpen ? "border-[#22FF65]" : "border-white/10"
      }`}
    >
      <button
        type="button"
        onClick={handleClick}
        className={`w-full px-6 py-5 text-left flex items-center justify-between transition-colors ${
          isOpen ? "bg-linear-to-b from-white/13 to-white/0 hover:bg-white/8" : "hover:bg-white/5"
        }`}
      >
        <span className="text-white font-medium text-lg pr-4">{question}</span>
        <ChevronDown className={`h-5 w-5 text-gray-400 transition-transform shrink-0 ${isOpen ? "rotate-180" : ""}`} />
      </button>
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            key="faq-content"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            {/* Divider between header and content */}
            <div className="w-[89%] h-px bg-white/8 mx-auto" />
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.1, duration: 0.2 }}
              className="px-6 pb-6 text-white/80 mt-[1.2rem] space-y-4 leading-[1.35rem]"
            >
              {typeof answer === "string" ? <p>{answer}</p> : answer}
              {/* Relative update timestamp */}
              <p className="text-[.8rem] text-white/50 font-medium text-right w-full">{updateText}</p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
})
